import { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faPaperPlane,
  faTimesCircle,
  faCloudUploadAlt,
  faCheckCircle,
  faExclamationTriangle,
  faShieldAlt,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomButton from "../../Components/CustomButton";
import { formatDate } from "../../utils/dateUtils";
import "./style.css";

const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const MIN_SIZE_BYTES = 1024; // 1KB minimum — blocks empty/corrupt PDFs

const EMAIL_MAX_LENGTH = 254;
const SUBJECT_MIN_LENGTH = 3;
const SUBJECT_MAX_LENGTH = 150;

// RFC 5322 compliant email regex
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

// Only printable, safe characters for email subject
const SUBJECT_REGEX = /^[a-zA-Z0-9\s\-_.,!?#&():'"/]+$/;

const PdfMailSender = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [sizeError, setSizeError] = useState("");
  const [fileError, setFileError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [subject, setSubject] = useState("");
  const [subjectError, setSubjectError] = useState("");
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  const toastId = useRef(null);
  const token = localStorage.getItem("login") || "";
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const showToast = (type, msg) => {
    if (toastId.current) toast.dismiss(toastId.current);
    toastId.current = toast[type](msg, {
      autoClose: 3000,
      position: "top-right",
    });
  };

  // ── Fetch logs on mount ──────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await fetch(`${apiUrl}/admin/pdf-email-logs`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const data = await res.json();
      if (data?.status) setLogs(data.data);
    } catch {
      // non-critical — table simply stays empty
    } finally {
      setLogsLoading(false);
    }
  }, [apiUrl, token]);

  // Fixed: was incorrectly using useState instead of useEffect
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // ── Dropzone ─────────────────────────────────────────────────────────────
  const onDrop = useCallback((accepted, rejected) => {
    setSizeError("");
    setFileError("");
    if (rejected.length > 0) {
      const err = rejected[0]?.errors?.[0];
      if (err?.code === "file-too-large") {
        setSizeError(`File exceeds ${MAX_SIZE_MB}MB limit.`);
      } else if (err?.code === "file-invalid-type") {
        setSizeError("Only PDF files are accepted.");
      } else {
        setSizeError("Invalid file. Please upload a PDF under 10MB.");
      }
      return;
    }
    if (accepted.length > 0) {
      const file = accepted[0];

      // Extra check: minimum size
      if (file.size < MIN_SIZE_BYTES) {
        setSizeError("File appears to be empty or corrupt (less than 1KB).");
        return;
      }

      // Extra check: file name sanity
      const namePart = file.name.replace(/\.pdf$/i, "");
      if (!/^[a-zA-Z0-9\s\-_().]+$/.test(namePart)) {
        setSizeError(
          "File name contains invalid characters. Please rename your file."
        );
        return;
      }

      setSelectedFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    maxSize: MAX_SIZE_BYTES,
  });

  const removeFile = () => {
    setSelectedFile(null);
    setSizeError("");
    setFileError("");
  };

  // ── Validation helpers ────────────────────────────────────────────────────
  const validateEmail = (val) => {
    const trimmed = val.trim();
    if (!trimmed) return "Recipient email is required.";
    if (trimmed.length > EMAIL_MAX_LENGTH)
      return `Email must not exceed ${EMAIL_MAX_LENGTH} characters.`;
    if (!EMAIL_REGEX.test(trimmed))
      return "Please enter a valid email address (e.g. user@example.com).";
    return "";
  };

  const validateSubject = (val) => {
    const trimmed = val.trim();
    if (!trimmed) return "Subject is required.";
    if (trimmed.length < SUBJECT_MIN_LENGTH)
      return `Subject must be at least ${SUBJECT_MIN_LENGTH} characters.`;
    if (trimmed.length > SUBJECT_MAX_LENGTH)
      return `Subject must not exceed ${SUBJECT_MAX_LENGTH} characters.`;
    if (!SUBJECT_REGEX.test(trimmed))
      return "Subject contains invalid characters. Use only letters, numbers, spaces, and basic punctuation.";
    return "";
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    // Block spaces at the beginning
    if (val.length === 1 && val === " ") return;
    setEmail(val);
    if (emailError) setEmailError(validateEmail(val));
  };

  const handleSubjectChange = (e) => {
    const val = e.target.value;
    // Enforce max length
    if (val.trim().length > SUBJECT_MAX_LENGTH) return;
    setSubject(val);
    if (subjectError) setSubjectError(validateSubject(val));
  };

  // ── Send with confirmation ───────────────────────────────────────────────
  const handleSendClick = () => {
    // Validate all fields at once so user sees ALL errors together
    const eErr = validateEmail(email);
    const sErr = validateSubject(subject);
    const fErr = !selectedFile ? "PDF attachment is required." : "";

    setEmailError(eErr);
    setSubjectError(sErr);
    setFileError(fErr);

    // Collect missing field names for summary toast
    const missing = [];
    if (eErr) missing.push("Email");
    if (sErr) missing.push("Subject");
    if (fErr) missing.push("PDF File");

    if (missing.length > 0) {
      showToast(
        "error",
        missing.length === 3
          ? "Please fill in all required fields before sending."
          : `Please fix the following: ${missing.join(", ")}.`
      );
      return;
    }

    // All valid — show confirmation dialog
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirm(false);

    const formData = new FormData();
    formData.append("email", email.trim());
    formData.append("subject", subject.trim());
    formData.append("pdf", selectedFile);

    setSending(true);
    try {
      const response = await axios.post(
        `${apiUrl}/admin/send-pdf-email`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.status) {
        setSendSuccess(true);
        showToast("success", "Email sent successfully!");

        // Reset form after success animation
        setTimeout(() => {
          setSelectedFile(null);
          setEmail("");
          setSubject("");
          setSizeError("");
          setFileError("");
          setEmailError("");
          setSubjectError("");
          setSendSuccess(false);
          fetchLogs();
        }, 2000);
      } else {
        showToast("error", response.data?.message || "Failed to send email.");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        "Failed to send email. Please try again.";
      showToast("error", msg);
    } finally {
      setSending(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  // Button is always clickable for validation feedback.
  // Only truly disabled while actively sending or showing success animation.
  const isBusy = sending || sendSuccess;

  const subjectCharCount = subject.trim().length;

  return (
    <DashboardLayout>
      <div className="pms-wrapper">
        {/* ── SEND FORM ─────────────────────────────────────────── */}
        <div className={`dashCard mb-4 ${sendSuccess ? "pms-success-glow" : ""}`}>
          <h2 className="mainTitle mb-4">PDF Mail Sender</h2>

          {/* Success overlay */}
          {sendSuccess && (
            <div className="pms-success-overlay">
              <div className="pms-success-icon-wrap">
                <FontAwesomeIcon icon={faCheckCircle} className="pms-success-icon" />
              </div>
              <p className="pms-success-text">Email Sent Successfully!</p>
            </div>
          )}

          <div className={`row g-4 ${sendSuccess ? "pms-form-blur" : ""}`}>
            {/* LEFT — Dropzone */}
            <div className="col-lg-5">
              <label className="pms-label">PDF Attachment</label>

              {!selectedFile ? (
                <div
                  {...getRootProps()}
                  className={`pms-dropzone ${
                    isDragActive ? "pms-dropzone--active" : ""
                  } ${sizeError || fileError ? "pms-dropzone--error" : ""}`}
                >
                  <input {...getInputProps()} />
                  <div className="pms-dropzone-inner">
                    <FontAwesomeIcon
                      icon={faCloudUploadAlt}
                      className="pms-upload-icon"
                    />
                    {isDragActive ? (
                      <p className="pms-drop-hint">Drop the PDF here…</p>
                    ) : (
                      <>
                        <p className="pms-drop-hint">
                          Drag & drop a PDF here
                        </p>
                        <p className="pms-drop-sub">
                          or click to browse files
                        </p>
                        <span className="pms-size-cap">
                          Max {MAX_SIZE_MB}MB · PDF only
                        </span>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="pms-file-preview">
                  <div className="pms-file-icon-wrap">
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      className="pms-pdf-icon"
                    />
                  </div>
                  <div className="pms-file-meta">
                    <p className="pms-file-name">{selectedFile.name}</p>
                    <p className="pms-file-size">
                      {formatBytes(selectedFile.size)}
                    </p>
                  </div>
                  <button
                    className="pms-remove-btn"
                    onClick={removeFile}
                    title="Remove file"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} />
                  </button>
                </div>
              )}

              {(sizeError || fileError) && (
                <p className="pms-field-error mt-2">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="me-1"
                  />
                  {sizeError || fileError}
                </p>
              )}

              <p className="pms-brand-note mt-2">
                The uploaded PDF is sent with the My Virtual PI logo header and
                footer applied. Its original content stays exactly the same.
              </p>
            </div>

            {/* RIGHT — Email form */}
            <div className="col-lg-7">
              {/* Recipient Email */}
              <div className="mb-3">
                <label className="pms-label">User Email Address</label>
                <div className="pms-input-wrapper">
                  <input
                    type="email"
                    className={`pms-input ${
                      emailError ? "pms-input--error" : ""
                    }`}
                    placeholder="example@gmail.com"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={() => setEmailError(validateEmail(email))}
                    maxLength={EMAIL_MAX_LENGTH}
                    autoComplete="email"
                  />
                  <span
                    className={`pms-char-counter ${
                      email.trim().length > EMAIL_MAX_LENGTH - 20
                        ? "pms-char-counter--warn"
                        : ""
                    }`}
                  >
                    {email.trim().length}/{EMAIL_MAX_LENGTH}
                  </span>
                </div>
                {emailError && (
                  <p className="pms-field-error">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="me-1"
                    />
                    {emailError}
                  </p>
                )}
              </div>

              {/* Subject */}
              <div className="mb-4">
                <label className="pms-label">Subject</label>
                <div className="pms-input-wrapper">
                  <input
                    type="text"
                    className={`pms-input ${
                      subjectError ? "pms-input--error" : ""
                    }`}
                    placeholder="Enter email subject"
                    value={subject}
                    onChange={handleSubjectChange}
                    onBlur={() => setSubjectError(validateSubject(subject))}
                    maxLength={SUBJECT_MAX_LENGTH + 5}
                  />
                  <span
                    className={`pms-char-counter ${
                      subjectCharCount > SUBJECT_MAX_LENGTH - 20
                        ? "pms-char-counter--warn"
                        : ""
                    } ${
                      subjectCharCount >= SUBJECT_MAX_LENGTH
                        ? "pms-char-counter--danger"
                        : ""
                    }`}
                  >
                    {subjectCharCount}/{SUBJECT_MAX_LENGTH}
                  </span>
                </div>
                {subjectError && (
                  <p className="pms-field-error">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="me-1"
                    />
                    {subjectError}
                  </p>
                )}
              </div>

              {/* Send button */}
              <div className="d-flex justify-content-end">
                <CustomButton
                  variant="primaryButton"
                  text={sending ? "Sending…" : "Send Email"}
                  icon={
                    !sending && (
                      <FontAwesomeIcon
                        icon={faPaperPlane}
                        className="ms-2"
                      />
                    )
                  }
                  onClick={handleSendClick}
                  disabled={isBusy}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── CONFIRMATION MODAL ──────────────────────────────────── */}
        {showConfirm && (
          <div className="pms-confirm-overlay" onClick={handleCancelConfirm}>
            <div
              className="pms-confirm-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pms-confirm-icon-wrap">
                <FontAwesomeIcon icon={faEnvelope} className="pms-confirm-icon" />
              </div>
              <h3 className="pms-confirm-title">Confirm Email</h3>
              <div className="pms-confirm-details">
                <div className="pms-confirm-row">
                  <span className="pms-confirm-label">To:</span>
                  <span className="pms-confirm-value">{email.trim()}</span>
                </div>
                <div className="pms-confirm-row">
                  <span className="pms-confirm-label">Subject:</span>
                  <span className="pms-confirm-value">{subject.trim()}</span>
                </div>
                <div className="pms-confirm-row">
                  <span className="pms-confirm-label">Attachment:</span>
                  <span className="pms-confirm-value">
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      className="text-danger me-1"
                    />
                    {selectedFile?.name}
                  </span>
                </div>
              </div>
              <div className="pms-confirm-actions">
                <button
                  className="pms-confirm-btn pms-confirm-btn--cancel"
                  onClick={handleCancelConfirm}
                >
                  Cancel
                </button>
                <button
                  className="pms-confirm-btn pms-confirm-btn--send"
                  onClick={handleConfirmSend}
                >
                  <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                  Send Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── EMAIL LOG TABLE ────────────────────────────────────── */}
        <div className="dashCard">
          <h2 className="mainTitle mb-4">Email Activity Log</h2>

          {logsLoading ? (
            <div className="text-center py-4">
              <span className="spinner-border" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-muted text-center py-4">
              No emails sent yet.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle pms-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Subject</th>
                    <th>File Name</th>
                    <th>File Size</th>
                    <th>Status</th>
                    <th>Sent At</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={log.id}>
                      <td>{i + 1}</td>
                      <td>{log.recipient_email}</td>
                      <td className="pms-subject-cell">{log.subject}</td>
                      <td>
                        <FontAwesomeIcon
                          icon={faFilePdf}
                          className="text-danger me-1"
                        />
                        {log.file_name}
                      </td>
                      <td>{formatBytes(log.file_size)}</td>
                      <td>
                        <span
                          className={`pms-status-badge pms-status-badge--${log.status}`}
                        >
                          {log.status === "sent" ? (
                            <>
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="me-1"
                              />
                              Sent
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="me-1"
                              />
                              Failed
                            </>
                          )}
                        </span>
                        {log.status === "failed" && log.error_message && (
                          <p className="pms-log-error-msg">
                            {log.error_message}
                          </p>
                        )}
                      </td>
                      <td>{formatDate(log.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PdfMailSender;
